const formatHashAsUuid = (hash: string): string =>
  `${hash.slice(0, 8)}-${hash.slice(8, 12)}-${hash.slice(12, 16)}-${hash.slice(16, 20)}-${hash.slice(20, 32)}`;

const hashToUuid = async (value: string): Promise<string> => {
  const subtleCrypto = globalThis.crypto?.subtle;
  if (!subtleCrypto) {
    throw new Error('Web Crypto API is unavailable');
  }

  const hashBuffer = await subtleCrypto.digest('SHA-256', new TextEncoder().encode(value));
  const hash = Array.from(new Uint8Array(hashBuffer), (byte) => byte.toString(16).padStart(2, '0')).join('');

  return formatHashAsUuid(hash);
};

const decodeBase64Url = (value: string): string | null => {
  const decoder = globalThis.atob;
  if (typeof decoder !== 'function') {
    return null;
  }

  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = decoder(padded);
    return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
  } catch {
    return null;
  }
};

/**
 * Reads the GoTrue `session_id` claim out of an access token.
 *
 * The claim is read, not verified — this value is only ever used as a lookup key for
 * connected-room state, never as proof of identity. Signaling trusts web-next as the auth
 * boundary and treats the forwarded token purely as a correlation handle, so decoding here
 * is the same trust level as hashing the token was.
 *
 * Returns null for anything that is not a JWT carrying the claim, which covers the non-token
 * callers (promotion session ids, program observer tokens).
 */
const extractGoTrueSessionId = (sessionToken: string): string | null => {
  const segments = sessionToken.split('.');
  if (segments.length !== 3) {
    return null;
  }

  const payloadJson = decodeBase64Url(segments[1]);
  if (!payloadJson) {
    return null;
  }

  try {
    const payload = JSON.parse(payloadJson) as { session_id?: unknown };
    return typeof payload.session_id === 'string' && payload.session_id.length > 0
      ? payload.session_id
      : null;
  } catch {
    return null;
  }
};

/**
 * Derives the runtime session id used by connected-room state from a raw auth session token.
 *
 * #3277 — this used to hash the whole access token, which meant the key changed every time
 * GoTrue rotated the token. `getSessionWithRefresh()` rotates ~5 minutes before expiry, and
 * the request that triggers the rotation is the same one that forwards the new token to
 * signaling, so the host's own polling killed its identity roughly 55 minutes after login:
 * every host-gated route began answering `403 user not connected to room` and only a page
 * reload (a new socket, re-registered under the new hash) recovered it.
 *
 * `session_id` is stable across refresh — GoTrue rotates the access and refresh tokens while
 * keeping the same session row — so keying on it survives rotation.
 *
 * Non-JWT inputs keep hashing the whole value, preserving the previous behaviour for the
 * callers that pass opaque ids rather than access tokens.
 */
export const sessionTokenToRuntimeSessionId = async (sessionToken: string): Promise<string> =>
  hashToUuid(extractGoTrueSessionId(sessionToken) ?? sessionToken);

/**
 * The pre-#3277 derivation: a hash of the whole token.
 *
 * Retained so lookups can still find users whose socket registered before the deploy that
 * changed the derivation. Without this, rolling out #3277 would itself break every in-flight
 * room in exactly the way #3277 fixes. Safe to delete once no pre-deploy socket can be live.
 */
export const legacySessionTokenToRuntimeSessionId = async (sessionToken: string): Promise<string> =>
  hashToUuid(sessionToken);
